/**
 * narrowDocumentTypes — zawężenie listy typów dokumentu (picker „nowy dokument") do zbioru id
 * z kontekstu listy (`useDocumentTypeNarrowing`: URL `?documentTypeIds=`/legacy
 * `?documentTypeId=` + filtr stały pozycji menu).
 *
 * Reguła jest LUSTREM serwera (`expandDocumentTypeIdsWithLocalCopies`,
 * `apps/api/lib/documents/document-type-filter.ts`), którym lista dokumentów rozszerza to samo
 * zawężenie — inaczej lista pokazuje dokumenty firmy, a picker „Dodaj" twierdzi, że żaden typ
 * nie pasuje. Wiersz przechodzi, gdy:
 *   1. jego id jest w zawężeniu (lokalne id podane wprost działa tak samo jak globalne), ALBO
 *   2. jest typem LOKALNYM, a jego `(klasa, lower(kod))` równa się `(klasa, lower(kod))` typu
 *      GLOBALNEGO, którego id jest w zawężeniu — to lokalna kopia typu globalnego (firma
 *      skopiowała typ globalny pod tym samym kodem).
 * Id niebędące typem globalnym NIE rozszerzają zawężenia (tak samo jak na serwerze).
 *
 * Dlaczego osobny `catalog`: lokalna kopia PRZESŁANIA typ globalny w swojej firmie — wiersz
 * globalny ma wtedy `selectable: false` i hook `useDocumentTypes` go odsiewa z listy wyboru.
 * Kod typu globalnego trzeba więc czytać z pełnego katalogu odpowiedzi (`allRows`), nie z listy
 * wybieralnych wierszy. `catalog` służy WYŁĄCZNIE do mapowania id globalne → kod; do wyboru
 * trafiają tylko wiersze z `rows`.
 *
 * Id są porównywane bez rozróżniania wielkości liter i po przycięciu (serwer normalizuje je tak
 * samo w `parseDocumentTypeIds`), kod — bez rozróżniania wielkości liter (jak `lower(code)` w SQL).
 * Puste zawężenie = brak zawężenia (zwraca `rows` bez zmian).
 */

import type { MergedDocumentTypeRow } from '@saas/shared';

/** Minimalny kształt wiersza potrzebny regule — wystarcza `Pick` ze scalonego wiersza typu. */
export type NarrowableDocumentType = Pick<
  MergedDocumentTypeRow,
  'id' | 'class' | 'code' | 'origin'
>;

function normalizeId(id: string): string {
  return id.trim().toLowerCase();
}

/** Klucz „ta sama rodzina typu": klasa + kod bez rozróżniania wielkości liter. */
function classCodeKey(type: Pick<NarrowableDocumentType, 'class' | 'code'>): string {
  return `${type.class}|${type.code.toLowerCase()}`;
}

export function narrowDocumentTypes<T extends NarrowableDocumentType>(
  rows: T[],
  catalog: readonly NarrowableDocumentType[],
  narrowingIds: readonly string[],
): T[] {
  const ids = new Set(narrowingIds.map(normalizeId).filter((id) => id !== ''));
  if (ids.size === 0) return rows;

  // (klasa, kod) typów GLOBALNYCH z zawężenia — tylko one rozszerzają zawężenie o lokalne kopie.
  const globalKeys = new Set<string>();
  for (const type of catalog) {
    if (type.origin === 'global' && ids.has(normalizeId(type.id))) {
      globalKeys.add(classCodeKey(type));
    }
  }

  return rows.filter(
    (row) =>
      ids.has(normalizeId(row.id)) ||
      (row.origin !== 'global' && globalKeys.has(classCodeKey(row))),
  );
}
